// Admin verification queue — review, view document, approve/reject/request-info.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, CheckCircle2, XCircle, AlertCircle, Clock, Download, Settings } from "lucide-react";
import { adminApi } from "@/lib/adminAuth";

const usd = (n) => Number(n || 0).toLocaleString();
const DOC_LABELS = {
    jamaican_drivers_license: "Driver's licence",
    jamaican_voter_id: "Voter ID",
    jamaican_passport: "Passport",
    jamaican_national_id: "National ID",
    utility_bill: "Utility bill",
    bank_statement: "Bank statement",
    government_correspondence: "Gov. correspondence",
    other: "Other",
};
const STATUS_COLOR = {
    pending_review: "bg-blue-100 text-blue-800",
    approved: "bg-jade/15 text-jade",
    rejected: "bg-red-100 text-red-700",
    info_required: "bg-amber-100 text-amber-800",
    expired: "bg-neutral-200 text-neutral-600",
    not_submitted: "bg-navy/10 text-navy/60",
};
const REJECT_REASONS = [
    "Name does not match reservation",
    "Document is unclear or unreadable",
    "Document is expired",
    "Address is not in Jamaica",
    "Document type is not accepted",
    "Information appears altered",
    "Supporting document is too old",
    "Primary guest is not eligible",
];

export default function AdminVerification() {
    const [rows, setRows] = useState([]);
    const [config, setConfig] = useState(null);
    const [filter, setFilter] = useState("pending_review");
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [docUrl, setDocUrl] = useState(null);
    const [action, setAction] = useState(""); // approve | reject | info
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [showConfig, setShowConfig] = useState(false);

    const load = async () => {
        try {
            const [list, cfg] = await Promise.all([
                adminApi.get(`/admin/verifications${filter ? `?status=${filter}` : ""}`),
                adminApi.get("/admin/resident-rate/config"),
            ]);
            setRows(list.data);
            setConfig(cfg.data);
        } catch { toast.error("Could not load verifications"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const open = async (id) => {
        setSelected(id);
        setDocUrl(null);
        setDetail(null);
        setAction(""); setReason(""); setNotes("");
        try {
            const { data } = await adminApi.get(`/admin/verifications/${id}`);
            setDetail(data);
            // Prefetch document blob for inline preview
            try {
                const resp = await adminApi.get(`/admin/verifications/${id}/document`, { responseType: "blob" });
                setDocUrl(URL.createObjectURL(resp.data));
            } catch { /* doc unavailable */ }
        } catch { toast.error("Could not load detail"); }
    };

    const decide = async () => {
        if (!action) return toast.error("Choose an action");
        if ((action === "reject" || action === "info") && !reason.trim()) return toast.error("Reason is required");
        setBusy(true);
        try {
            const endpoint = action === "approve" ? "approve" : action === "reject" ? "reject" : "request-info";
            const { data } = await adminApi.post(`/admin/verifications/${selected}/${endpoint}`, {
                reason: reason.trim() || (action === "approve" ? "Approved" : ""),
                notes: notes.trim() || null,
            });
            if (action === "reject" && data.rate_adjustment_usd > 0) {
                toast.success(`Rejected — standard rate applied, +$${data.rate_adjustment_usd} due`);
            } else {
                toast.success("Decision recorded");
            }
            await load();
            if (detail?.id === selected) await open(selected);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save decision");
        } finally { setBusy(false); }
    };

    const filtered = useMemo(() => rows, [rows]);

    return (
        <div data-testid="admin-verification-page">
            <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Compliance</div>
                    <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Jamaican Resident verification</h1>
                    <p className="text-[#0b1f22]/60 mt-2 text-sm">
                        Review resident-rate submissions. Current discount: <b>{config?.discount_pct ?? "—"}%</b> off standard · Deadline: <b>{config?.deadline_hours ?? "—"}h</b> after booking · Flow: <b>{config?.verification_flow === "verify_before" ? "Verify first · then pay" : "Book now · verify after"}</b> · Reuse window: <b>{config?.verified_reuse_months ?? "—"}mo</b> · Status: <b>{config?.active ? "Active" : "Paused"}</b>
                    </p>
                </div>
                <button onClick={() => setShowConfig(true)} data-testid="ver-open-config" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full border border-navy/20 hover:bg-navy hover:text-ivory">
                    <Settings size={13} /> Configure resident rate
                </button>
            </header>

            {/* Filters */}
            <div className="mb-4 flex flex-wrap gap-2">
                {[
                    { k: "pending_review", label: "Pending" },
                    { k: "info_required", label: "Info required" },
                    { k: "approved", label: "Approved" },
                    { k: "rejected", label: "Rejected" },
                    { k: "", label: "All" },
                ].map((f) => (
                    <button
                        key={f.k}
                        onClick={() => setFilter(f.k)}
                        data-testid={`ver-filter-${f.k || "all"}`}
                        className={`text-[10px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border ${filter === f.k ? "bg-navy text-ivory border-navy" : "text-navy/70 border-navy/20 hover:border-gold"}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="grid lg:grid-cols-[380px_1fr] gap-6">
                <div className="bg-white border border-navy/10 rounded-2xl overflow-hidden max-h-[75vh] overflow-y-auto">
                    {filtered.length === 0 ? (
                        <div className="p-10 text-center text-navy/50">
                            <ShieldCheck size={22} className="mx-auto mb-2 text-navy/30" /> No submissions
                        </div>
                    ) : filtered.map((v) => (
                        <button
                            key={v.id}
                            onClick={() => open(v.id)}
                            data-testid={`ver-row-${v.id}`}
                            className={`w-full text-left px-5 py-4 border-b border-navy/10 hover:bg-navy/5 transition-colors ${selected === v.id ? "bg-navy/5" : ""}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-medium text-navy truncate">{v.full_legal_name}</div>
                                    <div className="text-[11px] text-navy/50 mt-0.5 truncate">{v.customer_email}</div>
                                    <div className="text-[11px] text-navy/50 mt-0.5">{v.reservation?.check_in} → {v.reservation?.check_out} · {v.reservation?.room_name}</div>
                                    {v.name_match_flag && <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-amber-700"><AlertCircle size={10} /> Name mismatch</div>}
                                </div>
                                <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full whitespace-nowrap ${STATUS_COLOR[v.status]}`}>{v.status?.replace("_", " ")}</span>
                            </div>
                        </button>
                    ))}
                </div>

                <div>
                    {!selected ? (
                        <div className="bg-white border border-navy/10 rounded-2xl p-10 text-center text-navy/50 text-sm">
                            Select a submission to review the document and apply a decision.
                        </div>
                    ) : !detail ? (
                        <div className="bg-white border border-navy/10 rounded-2xl p-10 text-sm text-navy/60">Loading…</div>
                    ) : (
                        <div className="space-y-4" data-testid="ver-detail">
                            <section className="bg-white border border-navy/10 rounded-2xl p-6">
                                <div className="flex items-start justify-between flex-wrap gap-3">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Reservation {detail.reservation?.confirmation_number}</div>
                                        <div className="font-display text-2xl text-navy mt-1">{detail.full_legal_name}</div>
                                        <div className="text-[12px] text-navy/60 mt-1">{detail.customer_email}</div>
                                        <div className="text-[12px] text-navy/60 mt-0.5">{detail.reservation?.room_name} · {detail.reservation?.check_in} → {detail.reservation?.check_out}</div>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${STATUS_COLOR[detail.status]}`}>{detail.status?.replace("_", " ")}</span>
                                </div>
                                <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
                                    <Row label="Document type" value={DOC_LABELS[detail.doc_type] || detail.doc_type} />
                                    <Row label="Submitted" value={new Date(detail.submitted_at).toLocaleString()} />
                                    <Row label="Deadline" value={detail.deadline ? new Date(detail.deadline).toLocaleString() : "—"} />
                                    <Row label="Reservation total" value={`$${usd(detail.reservation?.total)}`} />
                                    <Row label="JA address" value={detail.ja_address} full />
                                    {detail.name_match_flag && <Row label="⚠ Name mismatch" value={`Guest on booking: ${detail.reservation?.guest_name}`} full />}
                                    {detail.decision_reason && <Row label="Reviewer reason" value={detail.decision_reason} full />}
                                </div>
                            </section>

                            <section className="bg-white border border-navy/10 rounded-2xl overflow-hidden">
                                <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Uploaded document</div>
                                    {docUrl && (
                                        <a href={docUrl} target="_blank" rel="noreferrer" className="text-[11px] uppercase tracking-[0.16em] text-gold inline-flex items-center gap-1"><Download size={11} /> Open full size</a>
                                    )}
                                </div>
                                <div className="bg-navy/[0.03] p-4 flex items-center justify-center min-h-[280px]">
                                    {docUrl ? (
                                        docUrl.match(/pdf/i) || detail.doc_type === "utility_bill" ? (
                                            <iframe src={docUrl} title="doc" data-testid="ver-doc-preview" className="w-full h-96 rounded-lg bg-white" />
                                        ) : (
                                            <img src={docUrl} data-testid="ver-doc-preview" alt="Verification document" className="max-h-96 rounded-lg" />
                                        )
                                    ) : (
                                        <div className="text-navy/50 text-sm">Fetching document…</div>
                                    )}
                                </div>
                            </section>

                            {["pending_review", "info_required"].includes(detail.status) && (
                                <section className="bg-white border border-navy/10 rounded-2xl p-6">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-3">Decision</div>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        <button data-testid="ver-approve" onClick={() => setAction("approve")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.16em] ${action === "approve" ? "bg-jade text-ivory" : "border border-jade text-jade hover:bg-jade/10"}`}>
                                            <CheckCircle2 size={13} /> Approve
                                        </button>
                                        <button data-testid="ver-info" onClick={() => setAction("info")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.16em] ${action === "info" ? "bg-amber-500 text-white" : "border border-amber-500 text-amber-700 hover:bg-amber-50"}`}>
                                            <AlertCircle size={13} /> Request info
                                        </button>
                                        <button data-testid="ver-reject" onClick={() => setAction("reject")} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.16em] ${action === "reject" ? "bg-red-500 text-white" : "border border-red-500 text-red-600 hover:bg-red-50"}`}>
                                            <XCircle size={13} /> Reject
                                        </button>
                                    </div>
                                    {(action === "reject" || action === "info") && (
                                        <div className="space-y-3">
                                            <label className="block">
                                                <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Reason (guest-visible)</span>
                                                <select data-testid="ver-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm">
                                                    <option value="">Choose or type below…</option>
                                                    {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                                                </select>
                                                <input type="text" placeholder="Custom reason…" value={reason.startsWith("Custom:") ? reason.replace("Custom: ", "") : ""} onChange={(e) => setReason(e.target.value ? `Custom: ${e.target.value}` : "")}
                                                    className="mt-2 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                                            </label>
                                            <label className="block">
                                                <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Internal notes (staff-only)</span>
                                                <textarea rows={2} data-testid="ver-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                                                    className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                                            </label>
                                        </div>
                                    )}
                                    {action && (
                                        <button onClick={decide} disabled={busy} data-testid="ver-submit-decision" className="mt-4 bg-navy text-ivory px-6 py-2.5 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-navy/90 disabled:opacity-60">
                                            {busy ? "Saving…" : `Confirm ${action === "info" ? "info request" : action}`}
                                        </button>
                                    )}
                                </section>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {showConfig && config && <ConfigModal config={config} onClose={() => setShowConfig(false)} onSaved={(c) => { setConfig(c); setShowConfig(false); load(); }} />}
        </div>
    );
}

function Row({ label, value, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-navy/50">{label}</div>
            <div className="text-navy mt-0.5">{value}</div>
        </div>
    );
}

function ConfigModal({ config, onClose, onSaved }) {
    const [f, setF] = useState({
        active: config.active,
        discount_pct: config.discount_pct,
        deadline_hours: config.deadline_hours,
        min_nights: config.min_nights || 1,
        max_nights: config.max_nights || 30,
        verification_flow: config.verification_flow || "verify_after",
        verified_reuse_months: config.verified_reuse_months ?? 3,
    });
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            const { data } = await adminApi.patch("/admin/resident-rate/config", {
                ...f,
                discount_pct: Number(f.discount_pct),
                deadline_hours: Number(f.deadline_hours),
                min_nights: Number(f.min_nights),
                max_nights: Number(f.max_nights),
                verified_reuse_months: Number(f.verified_reuse_months),
            });
            toast.success("Configuration saved");
            onSaved(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ver-config-modal">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="font-display text-2xl text-navy">Configure resident rate</h3>
                <p className="text-[11px] text-navy/60 mt-1">Discount is applied against the flexible standard rate.</p>
                <div className="mt-5 space-y-4">
                    <label className="flex items-center gap-3 text-sm text-navy/70 cursor-pointer">
                        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-jade w-4 h-4" data-testid="cfg-active" />
                        <span>Resident rate is <b>{f.active ? "active" : "paused"}</b></span>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Discount %</span>
                        <input type="number" min={0} max={90} step={1} value={f.discount_pct} onChange={(e) => setF({ ...f, discount_pct: e.target.value })} data-testid="cfg-pct" className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Verification deadline (hours after booking)</span>
                        <input type="number" min={1} max={168} step={1} value={f.deadline_hours} onChange={(e) => setF({ ...f, deadline_hours: e.target.value })} data-testid="cfg-deadline" className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Min nights</span>
                            <input type="number" min={1} value={f.min_nights} onChange={(e) => setF({ ...f, min_nights: e.target.value })} className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Max nights</span>
                            <input type="number" min={1} value={f.max_nights} onChange={(e) => setF({ ...f, max_nights: e.target.value })} className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                        </label>
                    </div>

                    <div className="pt-3 mt-3 border-t border-navy/10">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Verification workflow</div>
                        <div className="space-y-2">
                            <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${f.verification_flow === "verify_after" ? "border-jade bg-jade/5" : "border-navy/15 hover:border-navy/30"}`}>
                                <input type="radio" name="flow" checked={f.verification_flow === "verify_after"} onChange={() => setF({ ...f, verification_flow: "verify_after" })} data-testid="cfg-flow-after" className="mt-1 accent-jade" />
                                <div className="text-sm">
                                    <div className="font-medium text-navy">Book Now · Verify After</div>
                                    <div className="text-[11px] text-navy/60 mt-0.5">Guests pay the resident rate immediately; upload docs within the deadline. Rejected verifications revert to standard rate.</div>
                                </div>
                            </label>
                            <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${f.verification_flow === "verify_before" ? "border-jade bg-jade/5" : "border-navy/15 hover:border-navy/30"}`}>
                                <input type="radio" name="flow" checked={f.verification_flow === "verify_before"} onChange={() => setF({ ...f, verification_flow: "verify_before" })} data-testid="cfg-flow-before" className="mt-1 accent-jade" />
                                <div className="text-sm">
                                    <div className="font-medium text-navy">Verify First · Then Pay</div>
                                    <div className="text-[11px] text-navy/60 mt-0.5">Payment is blocked for resident-rate bookings until staff have approved a document (or the guest has a reusable Verified status).</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <label className="block pt-1">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Reusable Verified status (months)</span>
                        <input type="number" min={0} max={60} step={1} value={f.verified_reuse_months} onChange={(e) => setF({ ...f, verified_reuse_months: e.target.value })} data-testid="cfg-reuse-months" className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                        <p className="text-[11px] text-navy/50 mt-1">Once approved, a resident can book again for this many months without re-uploading documents. Set to <b>0</b> to disable reuse.</p>
                    </label>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 text-sm text-navy hover:bg-navy/5 rounded-full">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="cfg-save" className="bg-navy text-ivory px-6 py-2.5 rounded-full text-[11px] uppercase tracking-[0.22em] disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
                </div>
            </div>
        </div>
    );
}
